import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  TextField,
} from '@mui/material';
import type { LegacyRef } from 'react';
import type React from 'react';
import { useState } from 'react';

export type TagLike = { id?: number; name: string };

export interface TagInputProps<T extends { id: number; name?: string }> {
  initialValues?: T[];
  initialTags?: TagLike[];
  predicate: (input: string[]) => (data: T) => boolean;
  selectionValues: T[];
  handleSelectedValues?: (values: T[]) => void;
}

function TagInput<T extends { id: number; name: string }>(
  {
    selectionValues = [],
    handleSelectedValues,
    predicate,
    initialTags,
  }: TagInputProps<T>,
  _ref: LegacyRef<HTMLDivElement> | undefined
) {
  const [tagList, setTagList] = useState<TagLike[]>(
    initialTags ? initialTags : []
  );
  const [filteredSelectionValues, setFilteredSelectionValues] =
    useState<T[]>(selectionValues);
  const [inputValue, setInputValue] = useState<string>('');

  const addTag = (value: string) => {
    if (!value) return;

    const tagAlreadyExists = tagList.some((tag) => tag.name === value);
    if (tagAlreadyExists) {
      setInputValue('');
      setFilteredSelectionValues(selectionValues || []);
      return;
    }

    const tagToAdd = selectionValues.find((tag) => tag.name === value) || {
      name: value,
    };
    const newTags = [...tagList, tagToAdd as TagLike];
    setTagList(newTags);
    handleSelectedValues?.(newTags as unknown as T[]);
    setInputValue('');
    setFilteredSelectionValues(selectionValues || []);
  };

  const deleteTag = (value: string) => {
    const newTags = tagList.filter((tag) => tag.name !== value);
    setTagList(newTags);

    const updatedSelections =
      selectionValues?.filter((item) =>
        newTags.some((tag) => tag.name === item.name)
      ) || [];
    handleSelectedValues?.(updatedSelections);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setFilteredSelectionValues(selectionValues.filter(predicate([value])));
  };

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue.trim());
    }
  };

  const onSuggestionClick = (name: string) => {
    addTag(name);
  };

  const showSuggestions =
    filteredSelectionValues.length > 0 && Boolean(inputValue);

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
        }}
      >
        {tagList.map((tag) => (
          <Chip
            key={tag.name}
            label={tag.name}
            size="small"
            onDelete={() => deleteTag(tag.name)}
            deleteIcon={<CloseIcon />}
          />
        ))}
        <TextField
          size="small"
          placeholder="Filtrer par tag"
          value={inputValue}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
          variant="standard"
          slotProps={{
            input: {
              disableUnderline: true,
            },
          }}
          sx={{ flex: '1 1 120px', minWidth: 120 }}
        />
      </Box>
      {showSuggestions && (
        <List
          dense
          sx={{
            mt: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: 160,
            overflow: 'auto',
          }}
        >
          {filteredSelectionValues
            .filter((s) => s.name !== undefined)
            .map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => onSuggestionClick(item.name)}
              >
                <ListItemText primary={item.name} />
              </ListItemButton>
            ))}
        </List>
      )}
    </Box>
  );
}

export default TagInput;
